import React, { Dispatch, useEffect, useState } from "react";
import BootstrapTable, { ColumnDescription, PaginationOptions, SelectRowProps } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import { useDispatch, useSelector } from "react-redux";
import { getFileExtension } from "../../../helper/Validations";
import { ApplicationState } from "../../../store";
import { FileFormatIcon } from "../../svg/FileIconCollection";
import { ArchiveConstants, DateFormatConstants, DEFAULT_PAGE_SIZE } from "../../../helper/Constants";
import { actionTypes } from "../../../types/folderTypes";
import { MessageSource, MyFilesFileType } from "../../../core/common/enums";
import DownloadModal from "./DownloadModal";
import Checkbox, { CheckBoxSize } from "../../common/ui/Checkbox";
import { IMyFilesFileModel } from "../../../core/viewModels/message/MessageFileViewModel";
import { IFolderModel } from "../../../core/viewModels/folder/FolderModel";
import { getFormattedLocalDateTime } from "../../../helper/DateHelperFunctions";
import { ResourceIdLocators } from "../../../helper/ResourceIdLocators";
import { formatBytes, getFileViewLoaderHeight, getFileViewWrapperHeight } from "../../../helper/HelperFunctions";

interface IMessageFileViewProps {
    downloadSingleAttachment?: (
        fileId: number,
        physicalPath: string,
        messageId: number,
        fileName: string,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        messageType?: number
    ) => void;
    handleDRDownloadSingleAttachment?: (fileGuid: string, sentDate: Date, fileName: string) => void;
    setShowDownloadModal: Dispatch<React.SetStateAction<boolean>>;
    showDownloadModal: boolean;
    selectedFiles: IMyFilesFileModel[];
    setSelectedFiles: Dispatch<React.SetStateAction<IMyFilesFileModel[]>>;
    currentFolder: IFolderModel;
    handleResetSearchAndSort: () => void;
    currentFilesPageNumber?: number;
    setCurrentPageNumber: Dispatch<React.SetStateAction<number>>;
    sortBy?: any;
    setSortBy?: (fileViewSort: any) => void;
}

interface FileViewColumnDescription extends ColumnDescription {
    headerTitle?: boolean | string;
}

const MessageFileView: React.FC<IMessageFileViewProps> = props => {
    const {
        downloadSingleAttachment,
        handleDRDownloadSingleAttachment,
        showDownloadModal,
        setShowDownloadModal,
        selectedFiles,
        setSelectedFiles,
        handleResetSearchAndSort,
        currentFolder,
        currentFilesPageNumber,
        setCurrentPageNumber,
    } = props;
    const dispatch = useDispatch();
    const { isLoading, fileData } = useSelector((state: ApplicationState) => state.folderState.fileViewData);
    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const [count, setCount] = useState<number>(0);

    const paginationOptions: PaginationOptions = {
        totalSize: count,
        sizePerPage: DEFAULT_PAGE_SIZE,
        page: currentFilesPageNumber,
        showTotal: false,
        withFirstAndLast: true,
        alwaysShowAllBtns: true,
    };

    useEffect(() => {
        setCount(fileData?.length ? fileData[0].count : 0);
    }, [fileData, currentFilesPageNumber]);

    useEffect(() => {
        return () => {
            // clean up code
            dispatch({
                type: actionTypes.RESET_MESSAGE_FILE_VIEW_DATA,
            });
        };
    }, []);

    const handlePageChange = (page: number) => {
        setCurrentPageNumber(page);
    };

    const fileNameFormatter = (cell: string, row: IMyFilesFileModel): JSX.Element => {
        return (
            <div
                className="file-icon-name-container"
                title={row.fileName}
                data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnDownloadFile}
                onClick={() => {
                    if (row.fileType === MyFilesFileType.DocumentRequest) {
                        handleDRDownloadSingleAttachment &&
                            handleDRDownloadSingleAttachment(row.fileGuid, row.sentOrReceivedDate, row.fileName);
                    } else {
                        downloadSingleAttachment &&
                            downloadSingleAttachment(
                                row.fileId,
                                row.fileGuid,
                                row.referenceId,
                                row.fileName,
                                row.senderEmail,
                                row.referenceGuid,
                                row.messageSource,
                                row.fileType
                            );
                    }
                }}
            >
                <FileFormatIcon extension={getFileExtension(cell)} width={40} height={40}></FileFormatIcon>
                <p className="overflowText">{row.fileName}</p>
            </div>
        );
    };

    const subjectFormatter = (cell: string, row: IMyFilesFileModel): JSX.Element => {
        const subject = row.fileType === MyFilesFileType.DocumentRequest ? "NA" : cell;
        return (
            <span className="subject-cell overflowText" title={subject}>
                {subject}
            </span>
        );
    };

    const documentRequestDataFormatter = (cell: string, row: IMyFilesFileModel): JSX.Element => {
        return (
            <span className="subject-cell overflowText" title={cell}>
                {row.fileType == MyFilesFileType.DocumentRequest ? cell ?? "-" : "NA"}
            </span>
        );
    };

    const documentRequestDateFormatter = (cell: Date, row: IMyFilesFileModel): JSX.Element => {
        const sentDate =
            row.fileType === MyFilesFileType.DocumentRequest
                ? cell != null
                    ? getFormattedLocalDateTime(cell, DateFormatConstants.DEFAULT_DATE_FORMAT)
                    : "-"
                : "NA";
        return <span className="sent-date-cell">{sentDate}</span>;
    };

    const sentDateFormatter = (cell: Date, row: IMyFilesFileModel): JSX.Element => {
        const sentDate =
            row.fileType === MyFilesFileType.ReceivedMessage || row.fileType === MyFilesFileType.SentMessage
                ? cell != null
                    ? getFormattedLocalDateTime(cell, DateFormatConstants.DEFAULT_DATE_FORMAT)
                    : "-"
                : "NA";
        return <span className="sent-date-cell">{sentDate}</span>;
    };

    const dateFormatter = (cell: Date): JSX.Element => {
        const sentDate = cell != null ? getFormattedLocalDateTime(cell, DateFormatConstants.DEFAULT_DATE_FORMAT) : "-";
        return <span className="sent-date-cell">{sentDate}</span>;
    };

    const downloadDateFormatter = (cell: Date): JSX.Element => {
        return (
            <span className="sent-date-cell">
                {cell != null ? getFormattedLocalDateTime(cell, DateFormatConstants.REPORTS_DATE_FORMAT) : "NA"}
            </span>
        );
    };

    const senderNameFormatter = (cell: string, row: any): JSX.Element => {
        const name = row.senderFirstName || row.senderLastName ? `${row.senderFirstName} ${row.senderLastName}` : "NA";
        return (
            <span title={name} className="sender-name-cell overflowText">
                {name}
            </span>
        );
    };

    const fileSizeFormatter = (cell: string): JSX.Element => {
        return <span className="filesize-cell">{formatBytes(Number(cell))}</span>;
    };

    const headerFormatter = (column: any, colIndex: number, { sortElement }: any): JSX.Element => {
        return (
            <>
                <span title={column.text}>{column.text}</span>
                {sortElement}
            </>
        );
    };

    const columns: FileViewColumnDescription[] = [
        {
            text: "File Name",
            dataField: "fileName",
            formatter: fileNameFormatter,
            sort: true,
            headerStyle: { width: "13%" },
            headerFormatter,
        },
        {
            text: "Subject",
            dataField: "subject",
            formatter: subjectFormatter,
            sort: true,
            headerStyle: { width: "10%" },
            headerFormatter,
        },
        {
            text: "Document Name",
            dataField: "documentName",
            formatter: documentRequestDataFormatter,
            sort: true,
            headerStyle: { width: "10%" },
            headerFormatter,
        },
        {
            text: "Document Category",
            dataField: "documentCategory",
            formatter: documentRequestDataFormatter,
            sort: true,
            headerStyle: { width: "10%" },
            headerFormatter,
        },
        {
            text: "Last Download",
            dataField: "lastDownload",
            formatter: downloadDateFormatter,
            sort: true,
            headerStyle: { width: "12%" },
            headerFormatter,
        },
        {
            text: "Size",
            dataField: "size",
            formatter: fileSizeFormatter,
            sort: true,
            headerStyle: { width: "7%" },
            headerFormatter,
        },
        {
            text: "Sender",
            dataField: "senderFirstName",
            formatter: senderNameFormatter,
            sort: true,
            headerStyle: { width: "10%" },
            headerFormatter,
        },
        {
            text: "Sent/Received Date",
            dataField: "sentOrReceivedDate",
            formatter: sentDateFormatter,
            sort: true,
            headerStyle: { width: "10%" },
            headerFormatter,
        },
        {
            text: "Uploaded By",
            dataField: "uploadedBy",
            formatter: documentRequestDataFormatter,
            sort: true,
            headerStyle: { width: "8%" },
            headerFormatter,
        },
        {
            text: "Uploaded Date",
            dataField: "uploadedDate",
            formatter: documentRequestDateFormatter,
            sort: true,
            headerStyle: { width: "10%" },
            headerFormatter,
        },
        {
            text: "Expire Date ",
            dataField: "expireDate",
            formatter: dateFormatter,
            sort: true,
            headerStyle: { width: "10%" },
            headerFormatter,
        },
    ];

    const setNoContent = () => {
        if (!isLoading && fileData.length === 0) {
            return ArchiveConstants.MessageView.NO_DATA_MESSAGE;
        }
    };

    const handleOnSelect = (row: IMyFilesFileModel, isSelect: boolean) => {
        let selectedFilesCopy = [...selectedFiles];
        if (isSelect) {
            selectedFilesCopy.push(row);
        } else {
            selectedFilesCopy = selectedFilesCopy.filter(file => file.rowId != row.rowId);
        }
        setSelectedFiles(selectedFilesCopy);
    };

    const handleOnSelectAll = (isSelect: boolean, rows: IMyFilesFileModel[]) => {
        if (isSelect) {
            setSelectedFiles(rows);
        } else {
            setSelectedFiles([]);
        }
    };

    const selectRow: SelectRowProps<any> = {
        mode: "checkbox",
        clickToSelect: true,
        nonSelectable: [],
        selected: selectedFiles.map(file => file.rowId),
        onSelect: handleOnSelect,
        onSelectAll: handleOnSelectAll,
        selectionHeaderRenderer: ({ indeterminate }) => (
            <Checkbox
                id="mpiCheckbox"
                size={CheckBoxSize.sm}
                checked={!!selectedFiles.length}
                indeterminate={indeterminate}
                onClick={e => e.stopPropagation()}
                onChange={e => e.stopPropagation()}
                text={""}
            />
        ),
    };

    return (
        <LoadingOverlay className="file-view-loader" style={{ height: getFileViewLoaderHeight(mailboxUserData) }}>
            <div
                className="archive-table-container table-responsive"
                style={{ maxHeight: getFileViewWrapperHeight(mailboxUserData) }}
            >
                <BootstrapTable
                    classes={"settings-table"}
                    keyField={"rowId"}
                    columns={columns}
                    data={fileData}
                    noDataIndication={setNoContent()}
                    bordered={false}
                    selectRow={selectRow}
                    sort={props.sortBy}
                    pagination={paginationFactory(paginationOptions)}
                    remote={{ sort: true, pagination: true }}
                    onTableChange={(type: string, { sortField, sortOrder, page }: any) => {
                        if (type === "sort") {
                            props.setSortBy && props.setSortBy({ dataField: sortField, order: sortOrder });
                        }
                        handlePageChange(page);
                    }}
                />
            </div>
            <DownloadModal
                show={showDownloadModal}
                onHide={() => setShowDownloadModal(false)}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                folder={currentFolder}
                resourceId={ResourceIdLocators.MyFiles.MyFilesBtnDownloadFiles}
                handleResetSearchAndSort={handleResetSearchAndSort}
            />

            <Loader loading={isLoading} />
        </LoadingOverlay>
    );
};

export default MessageFileView;
